// src/pages/restaurant-tables/LoungeTables.jsx
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  LinearProgress,
  TablePagination,
  Fade,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as AvailableIcon,
  Cancel as OccupiedIcon,
  EventAvailable as ReservedIcon,
  Build as MaintenanceIcon,
  Visibility as ViewIcon,
  Restaurant as TableIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

// Lazy load sidebars for better initial load
const LoungeSidebar = lazy(() => import('../../../../components/RestaurantPOS/manager/lounge/Sidebar_consultants'));
const Header = lazy(() => import('../../../../components/RestaurantPOS/manager/Header_Consultants'));
const Footer = lazy(() => import('../../../../components/RestaurantPOS/manager/Footer'));
const Preloader = lazy(() => import('../../../../theme/Preloader'));

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// ============================================
// CONSTANTS
// ============================================

const TABLE_STATUS = {
  AVAILABLE: 'AVAILABLE',
  OCCUPIED: 'OCCUPIED',
  RESERVED: 'RESERVED',
  MAINTENANCE: 'MAINTENANCE',
};

const STATUS_CONFIG = {
  [TABLE_STATUS.AVAILABLE]: {
    label: 'Available',
    color: 'success',
    icon: AvailableIcon,
    bgColor: '#e8f5e9',
  },
  [TABLE_STATUS.OCCUPIED]: {
    label: 'Occupied',
    color: 'error',
    icon: OccupiedIcon,
    bgColor: '#ffebee',
  },
  [TABLE_STATUS.RESERVED]: {
    label: 'Reserved',
    color: 'warning',
    icon: ReservedIcon,
    bgColor: '#fff3e0',
  },
  [TABLE_STATUS.MAINTENANCE]: {
    label: 'Maintenance',
    color: 'default',
    icon: MaintenanceIcon,
    bgColor: '#f5f5f5',
  },
};

// Lounge-only configuration
const LOUNGE_CONFIG = {
  key: 'lounge',
  id: 4,
  name: 'Lounge',
  color: '#6A1B9A',
  lightColor: '#9C27B0',
  icon: '🍸',
};

// ============================================
// TOKEN UTILITY FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

// ============================================
// SUB-COMPONENTS
// ============================================

const StatsCard = React.memo(({ stats }) => (
  <div className="stats" style={{ marginBottom: '20px' }}>
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="card-label" style={{ fontSize: '13px' }}>Total Tables</div>
      <div className="card-value" style={{ fontSize: '28px', fontWeight: 'bold' }}>{stats.total_tables}</div>
    </div>
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="card-label" style={{ fontSize: '13px' }}>Available</div>
      <div className="card-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4caf50' }}>
        {stats.available}
      </div>
    </div>
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="card-label" style={{ fontSize: '13px' }}>Occupied</div>
      <div className="card-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#f44336' }}>
        {stats.occupied}
      </div>
    </div>
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="card-label" style={{ fontSize: '13px' }}>Reserved</div>
      <div className="card-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff9800' }}>
        {stats.reserved}
      </div>
    </div>
  </div>
));

const TableCard = React.memo(({ table, onView, getStatusChip }) => {
  const statusKey = table.status?.toUpperCase() || TABLE_STATUS.AVAILABLE;
  const statusConfig = STATUS_CONFIG[statusKey] || STATUS_CONFIG[TABLE_STATUS.AVAILABLE];
  const borderColor =
    statusConfig.color === 'success' ? '#4caf50' :
    statusConfig.color === 'error' ? '#f44336' :
    statusConfig.color === 'warning' ? '#ff9800' : '#9e9e9e';

  return (
    <div
      className="panel"
      style={{
        borderLeft: `4px solid ${borderColor}`,
        transition: 'all 0.3s ease',
        cursor: 'default',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <Typography variant="h6" fontWeight="bold">
          {table.table_name}
        </Typography>
        {getStatusChip(table.status)}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
        <GroupIcon fontSize="small" color="action" />
        <Typography variant="body2">
          Capacity: <strong>{table.capacity}</strong> people
        </Typography>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
        <CalendarIcon fontSize="small" color="action" />
        <Typography variant="caption" color="textSecondary">
          Created: {table.created_date ? new Date(table.created_date).toLocaleDateString() : 'N/A'}
        </Typography>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <Tooltip title="View Details">
          <IconButton size="small" onClick={() => onView(table)} color="primary">
            <ViewIcon />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
});

// ============================================
// MAIN COMPONENT - View Only
// ============================================

const LoungeTables = () => {
  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Tables');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total_tables: 0,
    available: 0,
    occupied: 0,
    reserved: 0,
    maintenance: 0,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // View-only dialog
  const [viewDialog, setViewDialog] = useState({
    open: false,
    table: null,
  });

  const locationConfig = useMemo(() => LOUNGE_CONFIG, []);
  const { name: locationName, id: locationIdValue, color: locationColor, key: locationKey } = locationConfig;

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  const getAuthToken = useCallback(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || isTokenExpired(token)) return null;
      return token;
    } catch (err) {
      console.error('[LoungeTables] Error getting auth token:', err);
      return null;
    }
  }, []);

  const refreshAuthToken = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('No refresh token available');

      const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) throw new Error('Refresh failed');

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        return data.access_token;
      }
      throw new Error('No access token in refresh response');
    } catch (err) {
      console.error('[LoungeTables] Token refresh failed:', err);
      localStorage.clear();
      window.location.href = '/restaurantAuth';
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // ============================================
  // API REQUEST
  // ============================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    let token = getAuthToken();

    if (!token) {
      console.log('[LoungeTables] Token invalid, attempting refresh...');
      token = await refreshAuthToken();
      if (!token) throw new Error('Authentication required');
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const url = `${FULL_API_URL}${endpoint}`;

    try {
      const response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        console.log('[LoungeTables] Received 401, attempting token refresh...');
        const newToken = await refreshAuthToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, { ...options, headers });
          if (retryResponse.ok) {
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return await retryResponse.json();
            }
            return { message: await retryResponse.text() };
          }
          if (retryResponse.status === 401) {
            throw new Error('Authentication failed after refresh');
          }
        }
        throw new Error('Authentication failed');
      }

      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || 'Non-JSON response received' };
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (err) {
      console.error('[LoungeTables] API Error:', err);
      throw err;
    }
  }, [getAuthToken, refreshAuthToken]);

  // ============================================
  // FETCH
  // ============================================

  const fetchTables = useCallback(async () => {
    setLoadingTables(true);
    try {
      const data = await apiRequest(`/tables/location/${locationKey}`);
      const tablesData = Array.isArray(data) ? data : [];

      const filteredData = tablesData.filter(table =>
        table.location_id === locationIdValue ||
        (table.location && table.location.toLowerCase() === locationKey)
      );

      setTables(filteredData);
      setFilteredTables(filteredData);
      await fetchLocationStats();
    } catch (err) {
      console.error('[LoungeTables] Error fetching tables:', err);
      if (err.message === 'Authentication required' || err.message === 'Authentication failed after refresh') {
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      }
    } finally {
      setLoadingTables(false);
      setIsLoading(false);
    }
  }, [apiRequest, locationKey, locationIdValue]);

  const fetchLocationStats = useCallback(async () => {
    try {
      const statsData = await apiRequest(`/tables/location/${locationKey}/stats`);
      setStats({
        total_tables: statsData.total_tables || 0,
        available: statsData.available || 0,
        occupied: statsData.occupied || 0,
        reserved: statsData.reserved || 0,
        maintenance: statsData.maintenance || 0,
      });
    } catch (err) {
      console.error('[LoungeTables] Error fetching stats:', err);
    }
  }, [apiRequest, locationKey]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const getStatusChip = useCallback((status) => {
    const config = STATUS_CONFIG[status?.toUpperCase()] || STATUS_CONFIG[TABLE_STATUS.AVAILABLE];
    const Icon = config.icon;
    return (
      <Chip
        icon={<Icon />}
        label={config.label}
        color={config.color}
        size="small"
        sx={{
          fontWeight: 'medium',
          '& .MuiChip-icon': { fontSize: '16px' },
        }}
      />
    );
  }, []);

  const handleOpenView = useCallback((table) => {
    setViewDialog({ open: true, table });
  }, []);

  const handleCloseView = useCallback(() => {
    setViewDialog({ open: false, table: null });
  }, []);

  // ============================================
  // SESSION MANAGEMENT - Auto-refresh
  // ============================================

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();
      if (timeLeft > 0 && timeLeft < 120000) {
        console.log('[LoungeTables] Auto-refreshing token...');
        await refreshAuthToken();
      }
    };
    const interval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(interval);
  }, [refreshAuthToken]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // ============================================
  // RENDER
  // ============================================

  if (isLoading || isRefreshing) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Preloader text={`Loading ${locationName} Tables...`} size={70} fullScreen={true} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <LoungeSidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />

            <div className="epos-content">
              {/* Hero Card */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 'bold' }}>
                    {locationConfig.icon} {locationName} Tables
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      Overview
                    </div>
                  </div>
                </div>
              </div>

              <StatsCard stats={stats} />

              {loadingTables && (
                <div style={{ width: '100%', marginBottom: '16px' }}>
                  <LinearProgress color="primary" />
                </div>
              )}

              {filteredTables.length === 0 && !loadingTables ? (
                <div className="bottom-grid">
                  <div className="panel" style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <TableIcon style={{ fontSize: 60, color: '#94a3b8', marginBottom: '16px' }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No {locationName} Tables Found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      There are currently no tables for the {locationName} location.
                    </Typography>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bottom-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                    {filteredTables
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((table) => (
                        <TableCard
                          key={table.id}
                          table={table}
                          onView={handleOpenView}
                          getStatusChip={getStatusChip}
                        />
                      ))}
                  </div>

                  {filteredTables.length > rowsPerPage && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                      <TablePagination
                        component="div"
                        count={filteredTables.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* View-Only Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={handleCloseView}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ViewIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Table Details — {locationName}
            </Typography>
          </div>
        </DialogTitle>

        <DialogContent dividers>
          {viewDialog.table && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">Table Name:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {viewDialog.table.table_name}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">Location:</Typography>
                <Typography variant="body1">
                  {locationName} (ID: {locationIdValue})
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">Status:</Typography>
                {getStatusChip(viewDialog.table.status)}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">Capacity:</Typography>
                <Typography variant="body1">
                  {viewDialog.table.capacity} people
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">Created:</Typography>
                <Typography variant="body1">
                  {viewDialog.table.created_date
                    ? new Date(viewDialog.table.created_date).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Box>

              {viewDialog.table.description && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <DescriptionIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">Description:</Typography>
                    <Typography variant="body1">{viewDialog.table.description}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseView} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Suspense>
  );
};

export default LoungeTables;